// src/Store/WebSocketDataStore.js

import { create } from 'zustand';
import { sanitizeTimeSeriesBars } from '../Utils/barSeries';
import { isDuringMarketHours } from '../Utils/marketHours';
import { seriesGapRangesForDate } from '../Utils/seriesPresentation';

const ET_TIMEZONE = 'America/New_York';

const getMinuteStart = (timestamp) => {
  return timestamp - (timestamp % 60000);
};

function getEtDateKey(timestamp) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ET_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(timestamp));
}

function getCurrentEtDateKey() {
  return getEtDateKey(Date.now());
}

function filterCurrentTradingDayBars(bars) {
  const todayEt = getCurrentEtDateKey();

  return bars.filter((bar) => {
    const time = bar.time ?? bar.t;
    return typeof time === 'number' && getEtDateKey(time) === todayEt;
  });
}

function cloneIndexData(indexData = {}) {
  return Object.fromEntries(
    Object.entries(indexData || {}).map(([symbol, bars]) => [
      symbol,
      Array.isArray(bars) ? bars.map((bar) => ({ ...bar })) : bars,
    ])
  );
}

function indexBarMatches(left, right) {
  return left?.time === right?.time
    && left?.open === right?.open
    && left?.high === right?.high
    && left?.low === right?.low
    && left?.close === right?.close;
}

function indexDataMatches(left = {}, right = {}) {
  const symbols = new Set([
    ...Object.keys(left || {}),
    ...Object.keys(right || {}),
  ]);
  return [...symbols].every((symbol) => {
    const leftBars = left?.[symbol] ?? [];
    const rightBars = right?.[symbol] ?? [];
    return Array.isArray(leftBars)
      && Array.isArray(rightBars)
      && leftBars.length === rightBars.length
      && leftBars.every((bar, index) => indexBarMatches(bar, rightBars[index]));
  });
}

function cloneAggregationState(aggregationState = {}) {
  return Object.fromEntries(
    Object.entries(aggregationState || {}).map(([symbol, state]) => [
      symbol,
      state && typeof state === 'object' ? { ...state } : state,
    ])
  );
}

function buildIndexUpdate(state, symbol, secondUpdate) {
  const cleanSymbol = symbol.replace('I:', '');
  const currentTime = parseInt(secondUpdate.s, 10);

  if (!Number.isFinite(currentTime)) {
    return null;
  }
  if (cleanSymbol === 'SPX' && !isDuringMarketHours(currentTime)) {
    return null;
  }

  const minuteStart = getMinuteStart(currentTime);
  const incomingDate = getEtDateKey(currentTime);

  const currentState = state.aggregationState[cleanSymbol] || {};
  let updatedAggregation = { ...currentState };
  let indexDataUpdate = state.indexData;
  const existingBars = state.indexData[cleanSymbol] || [];
  const hasIncomingSessionBars = existingBars.some(
    (bar) => getEtDateKey(bar.time) === incomingDate
  );

  // Hydrated after-hours bars intentionally survive refreshes, but once the
  // first live tick for the current session lands we should clear that stale
  // day immediately instead of waiting for the first minute rollover.
  if (existingBars.length && !hasIncomingSessionBars) {
    indexDataUpdate = {
      ...state.indexData,
      [cleanSymbol]: [],
    };
  }

  if (!currentState.startTime || currentState.startTime !== minuteStart) {
    if (currentState.startTime) {
      const completedBar = {
        time: currentState.startTime,
        open: Number(currentState.open),
        high: Number(currentState.high),
        low: Number(currentState.low),
        close: Number(currentState.close),
      };

      const updatedBars = sanitizeTimeSeriesBars(
        [...existingBars, completedBar].filter(
          (bar) => getEtDateKey(bar.time) === incomingDate
        )
      );

      indexDataUpdate = {
        ...state.indexData,
        [cleanSymbol]: updatedBars,
      };
    }

    updatedAggregation = {
      startTime: minuteStart,
      open: Number(secondUpdate.o),
      high: Number(secondUpdate.h),
      low: Number(secondUpdate.l),
      close: Number(secondUpdate.c),
      // Preserve the source tick timestamp so lightweight consumers can
      // append the true streaming price path instead of only moving one
      // minute-bucket point.
      lastTickTime: currentTime,
    };
  } else {
    updatedAggregation = {
      ...currentState,
      high: Math.max(Number(currentState.high), Number(secondUpdate.h)),
      low: Math.min(Number(currentState.low), Number(secondUpdate.l)),
      close: Number(secondUpdate.c),
      // This changes on every streaming update, even while startTime remains
      // inside the same minute.
      lastTickTime: currentTime,
    };
  }

  const update = {
    indexData: indexDataUpdate,
    aggregationState: {
      ...state.aggregationState,
      [cleanSymbol]: updatedAggregation,
    },
  };
  if (cleanSymbol === 'SPX') {
    const hydration = state.indexHydration;
    const hasConfirmedHistory = (
      hasIncomingSessionBars
      && hydration?.tradingDate === incomingDate
      && (hydration.status === 'available' || hydration.status === 'retained')
    );
    update.indexHydration = hasConfirmedHistory
      ? hydration
      : {
        status: 'live',
        reason: 'live_update',
        tradingDate: incomingDate,
        coverage: null,
      };
  }
  return update;
}

function getInitialState() {
  return {
    indexData: {},
    aggregationState: {},
    // Bumped whenever a history payload changes bars that the realtime
    // subscription did not deliver. TradingView caches getBars results and only
    // refetches them after resetData.
    indexHistoryVersion: 0,
    indexHydration: { status: 'unavailable', reason: 'not_hydrated', tradingDate: null },
    connectionStatus: 'unknown',
    lastObservationAt: null,
    disconnectedAt: null,
    liveGapRanges: [],
    replayActive: false,
    _liveIndexDataSnapshot: null,
    _liveAggregationSnapshot: null,
    _replayBufferedIndexData: null,
    _replayBufferedAggregationState: null,
  };
}

const useWebSocketDataStore = create((set, get) => ({
  ...getInitialState(),

  setConnectionStatus: (connectionStatus, at = Date.now()) => set((state) => ({
    connectionStatus,
    disconnectedAt: ['disconnected', 'closed'].includes(connectionStatus)
      ? state.disconnectedAt ?? at
      : state.disconnectedAt,
  })),

  recordObservation: (at = Date.now()) => set((state) => {
    const observationAt = Number(at);
    if (!Number.isFinite(observationAt)) return state;
    const from = Number.isFinite(state.disconnectedAt)
      ? getMinuteStart(state.disconnectedAt) + 60_000
      : null;
    const to = getMinuteStart(observationAt);
    const gap = Number.isFinite(from)
      && to > from
      && getEtDateKey(from) === getEtDateKey(to)
      && isDuringMarketHours(from)
      && isDuringMarketHours(to)
      ? [{ from, to, source: 'index:SPX' }]
      : [];
    const retainedGapRanges = seriesGapRangesForDate(state.liveGapRanges, observationAt);
    const liveGapRanges = gap.length || retainedGapRanges.length !== state.liveGapRanges.length
      ? [...retainedGapRanges, ...gap]
      : state.liveGapRanges;
    return {
      connectionStatus: 'connected',
      lastObservationAt: observationAt,
      disconnectedAt: null,
      liveGapRanges,
    };
  }),

  setIndexData: (symbol, historicalBars) =>
    set((state) => {
      const cleanSymbol = symbol.replace('I:', '');

      if (!cleanSymbol || !Array.isArray(historicalBars)) {
        console.error(`[setIndexData] Invalid data for symbol: ${cleanSymbol}`, historicalBars);
        return state;
      }

      const validBars = historicalBars.filter(
        (bar) =>
          typeof bar.o === 'number' &&
          typeof bar.h === 'number' &&
          typeof bar.l === 'number' &&
          typeof bar.c === 'number' &&
          typeof bar.t === 'number'
      );

      if (validBars.length === 0) {
        return state;
      }

      const formattedBars = validBars.map((bar) => ({
        time: bar.t,
        open: bar.o,
        high: bar.h,
        low: bar.l,
        close: bar.c,
      }));

      const todayBars = filterCurrentTradingDayBars(sanitizeTimeSeriesBars(formattedBars));

      if (state.replayActive) {
        const baseIndexData = state._replayBufferedIndexData ?? state._liveIndexDataSnapshot ?? {};

        if (todayBars.length === 0 && baseIndexData[cleanSymbol]?.length) {
          return state;
        }

        return {
          _replayBufferedIndexData: {
            ...baseIndexData,
            [cleanSymbol]: todayBars,
          },
        };
      }

      // A history fetch that lands outside the session (after the close, overnight,
      // a weekend) filters down to nothing. Writing that empty array over the bars
      // dailyCache restored is what blanked the price chart after hours.
      if (todayBars.length === 0 && state.indexData[cleanSymbol]?.length) {
        return state;
      }

      return {
        indexData: {
          ...state.indexData,
          [cleanSymbol]: todayBars,
        },
        indexHistoryVersion: state.indexHistoryVersion + 1,
      };
    }),

  setHydratedIndexData: (payload, { historyUpdate = 'full' } = {}) =>
    set((state) => {
      const target = state.replayActive
        ? state._replayBufferedIndexData ?? state._liveIndexDataSnapshot ?? {}
        : state.indexData;
      const retainedBars = target.SPX || [];
      const retainedDate = retainedBars.length ? getEtDateKey(retainedBars[0].time) : null;
      const unavailableHydration = (reason, hydration) => {
        if (!retainedDate) return { ...hydration, status: 'unavailable', reason };
        return {
          status: 'retained',
          reason,
          tradingDate: retainedDate,
          coverage: state.indexHydration?.tradingDate === retainedDate
            ? state.indexHydration.coverage || null
            : null,
        };
      };
      const status = payload?.status === 'available' ? 'available' : 'unavailable';
      const tradingDate = typeof payload?.tradingDate === 'string' ? payload.tradingDate : null;
      const hydration = {
        status,
        reason: payload?.reason || null,
        tradingDate,
        coverage: payload?.coverage || null,
      };
      if (status !== 'available' || !tradingDate || !Array.isArray(payload?.bars)) {
        return { indexHydration: unavailableHydration(hydration.reason, hydration) };
      }

      const symbol = String(payload.symbol || '').replace('I:', '');
      if (!symbol) {
        return { indexHydration: unavailableHydration('invalid_symbol', hydration) };
      }
      const bars = sanitizeTimeSeriesBars(payload.bars).filter(
        (bar) => getEtDateKey(bar.time) === tradingDate
      );
      if (!bars.length) {
        return { indexHydration: unavailableHydration('empty', hydration) };
      }

      const existing = target[symbol] || [];
      const existingDate = existing.length ? getEtDateKey(existing[0].time) : null;
      const latestExistingTime = existing.length ? existing[existing.length - 1].time : null;
      const realtimeMinute = Number(state.aggregationState?.[symbol]?.startTime);
      const existingBarsByTime = new Map(existing.map((bar) => [bar.time, bar]));
      const repairsEarlierMinute = (
        historyUpdate === 'incremental'
        && existingDate === tradingDate
        && bars.some((bar) => (
          bar.time < latestExistingTime
          && !indexBarMatches(bar, existingBarsByTime.get(bar.time))
        ))
      );
      // subscribeBars already delivers the realtime aggregation minute. Only
      // hydration data beyond it needs a TradingView history reset.
      const extendsBeyondRealtimeMinute = (
        historyUpdate === 'incremental'
        && existingDate === tradingDate
        && bars.some((bar) => (
          bar.time > latestExistingTime
          && (!Number.isFinite(realtimeMinute) || bar.time > realtimeMinute)
        ))
      );
      const nextBars = sanitizeTimeSeriesBars([
        ...(existingDate === tradingDate ? existing : []),
        ...bars,
      ]);
      const update = {
        [state.replayActive ? '_replayBufferedIndexData' : 'indexData']: {
          ...target,
          [symbol]: nextBars,
        },
        indexHydration: hydration,
      };
      if (
        !state.replayActive
        && (
          historyUpdate === 'full'
          || repairsEarlierMinute
          || extendsBeyondRealtimeMinute
        )
      ) {
        update.indexHistoryVersion = state.indexHistoryVersion + 1;
      }
      return update;
    }),

  updateIndexData: (symbol, secondUpdate) =>
    set((state) => {
      if (state.replayActive) {
        const baseIndexData = state._replayBufferedIndexData ?? state._liveIndexDataSnapshot ?? {};
        const baseAggregationState =
          state._replayBufferedAggregationState ?? state._liveAggregationSnapshot ?? {};
        const update = buildIndexUpdate(
          {
            indexData: baseIndexData,
            aggregationState: baseAggregationState,
            indexHydration: state.indexHydration,
          },
          symbol,
          secondUpdate
        );

        if (!update) return state;

        return {
          _replayBufferedIndexData: update.indexData,
          _replayBufferedAggregationState: update.aggregationState,
          ...(update.indexHydration ? { indexHydration: update.indexHydration } : {}),
        };
      }

      return buildIndexUpdate(state, symbol, secondUpdate) ?? state;
    }),

  getIndexData: (symbol) => {
    const cleanSymbol = symbol.replace('I:', '');
    return get().indexData[cleanSymbol] || [];
  },

  clearIndexData: () => set({ indexData: {}, aggregationState: {} }),
  beginReplay: () =>
    set((state) => {
      if (state.replayActive) {
        return {
          indexData: {},
          aggregationState: {},
        };
      }

      const indexSnapshot = cloneIndexData(state.indexData);
      const aggregationSnapshot = cloneAggregationState(state.aggregationState);

      return {
        replayActive: true,
        indexData: {},
        aggregationState: {},
        _liveIndexDataSnapshot: indexSnapshot,
        _liveAggregationSnapshot: aggregationSnapshot,
        _replayBufferedIndexData: cloneIndexData(indexSnapshot),
        _replayBufferedAggregationState: cloneAggregationState(aggregationSnapshot),
      };
    }),
  setReplayActive: (replayActive) =>
    set((state) => {
      if (replayActive) {
        if (state.replayActive) return state;

        const indexSnapshot = cloneIndexData(state.indexData);
        const aggregationSnapshot = cloneAggregationState(state.aggregationState);

        return {
          replayActive: true,
          _liveIndexDataSnapshot: indexSnapshot,
          _liveAggregationSnapshot: aggregationSnapshot,
          _replayBufferedIndexData: cloneIndexData(indexSnapshot),
          _replayBufferedAggregationState: cloneAggregationState(aggregationSnapshot),
        };
      }

      if (!state.replayActive) {
        return {
          replayActive: false,
          _liveIndexDataSnapshot: null,
          _liveAggregationSnapshot: null,
          _replayBufferedIndexData: null,
          _replayBufferedAggregationState: null,
        };
      }

      const restoredIndexData = cloneIndexData(
        state._replayBufferedIndexData ?? state._liveIndexDataSnapshot ?? {}
      );
      const historyChanged = !indexDataMatches(state.indexData, restoredIndexData);

      return {
        replayActive: false,
        indexData: restoredIndexData,
        aggregationState: cloneAggregationState(
          state._replayBufferedAggregationState ?? state._liveAggregationSnapshot ?? {}
        ),
        ...(historyChanged
          ? { indexHistoryVersion: state.indexHistoryVersion + 1 }
          : {}),
        _liveIndexDataSnapshot: null,
        _liveAggregationSnapshot: null,
        _replayBufferedIndexData: null,
        _replayBufferedAggregationState: null,
      };
    }),
  reset: () => set(getInitialState()),
}));

export default useWebSocketDataStore;



// // src/Store/WebSocketDataStore.js
//
// import { create } from 'zustand';
// import { sanitizeTimeSeriesBars } from '../Utils/barSeries';
//
// const ET_TIMEZONE = 'America/New_York';
//
// const getMinuteStart = (timestamp) => {
//   return timestamp - (timestamp % 60000);
// };
//
// function getEtDateKey(timestamp) {
//   return new Intl.DateTimeFormat('en-CA', {
//     timeZone: ET_TIMEZONE,
//     year: 'numeric',
//     month: '2-digit',
//     day: '2-digit',
//   }).format(new Date(timestamp));
// }
//
// function getCurrentEtDateKey() {
//   return getEtDateKey(Date.now());
// }
//
// function isCurrentTradingDayBar(barTime) {
//   return getEtDateKey(barTime) === getCurrentEtDateKey();
// }
//
// function filterCurrentTradingDayBars(bars) {
//   const todayEt = getCurrentEtDateKey();
//
//   return bars.filter((bar) => {
//     const time = bar.time ?? bar.t;
//     return typeof time === 'number' && getEtDateKey(time) === todayEt;
//   });
// }
//
// function cloneIndexData(indexData = {}) {
//   return Object.fromEntries(
//     Object.entries(indexData || {}).map(([symbol, bars]) => [
//       symbol,
//       Array.isArray(bars) ? bars.map((bar) => ({ ...bar })) : bars,
//     ])
//   );
// }
//
// function cloneAggregationState(aggregationState = {}) {
//   return Object.fromEntries(
//     Object.entries(aggregationState || {}).map(([symbol, state]) => [
//       symbol,
//       state && typeof state === 'object' ? { ...state } : state,
//     ])
//   );
// }
//
// function buildIndexUpdate(state, symbol, secondUpdate) {
//   const cleanSymbol = symbol.replace('I:', '');
//   const currentTime = parseInt(secondUpdate.s, 10);
//
//   if (!Number.isFinite(currentTime)) {
//     return null;
//   }
//
//   const minuteStart = getMinuteStart(currentTime);
//
//   const currentState = state.aggregationState[cleanSymbol] || {};
//   let updatedAggregation = { ...currentState };
//   let indexDataUpdate = state.indexData;
//   const existingBars = state.indexData[cleanSymbol] || [];
//   const incomingIsCurrentDay = isCurrentTradingDayBar(currentTime);
//   const hasCurrentDayBars = existingBars.some((bar) => isCurrentTradingDayBar(bar.time));
//
//   // Hydrated after-hours bars intentionally survive refreshes, but once the
//   // first live tick for the current session lands we should clear that stale
//   // day immediately instead of waiting for the first minute rollover.
//   if (incomingIsCurrentDay && existingBars.length && !hasCurrentDayBars) {
//     indexDataUpdate = {
//       ...state.indexData,
//       [cleanSymbol]: [],
//     };
//   }
//
//   if (!currentState.startTime || currentState.startTime !== minuteStart) {
//     if (currentState.startTime) {
//       const completedBar = {
//         time: currentState.startTime,
//         open: Number(currentState.open),
//         high: Number(currentState.high),
//         low: Number(currentState.low),
//         close: Number(currentState.close),
//       };
//
//       const updatedBars = sanitizeTimeSeriesBars(
//         [...existingBars, completedBar].filter((bar) => isCurrentTradingDayBar(bar.time))
//       );
//
//       indexDataUpdate = {
//         ...state.indexData,
//         [cleanSymbol]: updatedBars,
//       };
//     }
//
//     updatedAggregation = {
//       startTime: minuteStart,
//       open: Number(secondUpdate.o),
//       high: Number(secondUpdate.h),
//       low: Number(secondUpdate.l),
//       close: Number(secondUpdate.c),
//     };
//   } else {
//     updatedAggregation = {
//       ...currentState,
//       high: Math.max(Number(currentState.high), Number(secondUpdate.h)),
//       low: Math.min(Number(currentState.low), Number(secondUpdate.l)),
//       close: Number(secondUpdate.c),
//     };
//   }
//
//   return {
//     indexData: indexDataUpdate,
//     aggregationState: {
//       ...state.aggregationState,
//       [cleanSymbol]: updatedAggregation,
//     },
//   };
// }
//
// function getInitialState() {
//   return {
//     indexData: {},
//     aggregationState: {},
//     // Bumped whenever a history payload REPLACES a series, as opposed to a tick
//     // extending it. TradingView caches the bars it fetched through getBars and
//     // only refetches on resetData, so a backfill that repairs an earlier stretch
//     // of the session is invisible without an explicit invalidation signal.
//     // Realtime ticks need none of this -- subscribeBars pushes those.
//     indexHistoryVersion: 0,
//     replayActive: false,
//     _liveIndexDataSnapshot: null,
//     _liveAggregationSnapshot: null,
//     _replayBufferedIndexData: null,
//     _replayBufferedAggregationState: null,
//   };
// }
//
// const useWebSocketDataStore = create((set, get) => ({
//   ...getInitialState(),
//
//   setIndexData: (symbol, historicalBars) =>
//     set((state) => {
//       const cleanSymbol = symbol.replace('I:', '');
//
//       if (!cleanSymbol || !Array.isArray(historicalBars)) {
//         console.error(`[setIndexData] Invalid data for symbol: ${cleanSymbol}`, historicalBars);
//         return state;
//       }
//
//       const validBars = historicalBars.filter(
//         (bar) =>
//           typeof bar.o === 'number' &&
//           typeof bar.h === 'number' &&
//           typeof bar.l === 'number' &&
//           typeof bar.c === 'number' &&
//           typeof bar.t === 'number'
//       );
//
//       if (validBars.length === 0) {
//         return state;
//       }
//
//       const formattedBars = validBars.map((bar) => ({
//         time: bar.t,
//         open: bar.o,
//         high: bar.h,
//         low: bar.l,
//         close: bar.c,
//       }));
//
//       const todayBars = filterCurrentTradingDayBars(sanitizeTimeSeriesBars(formattedBars));
//
//       if (state.replayActive) {
//         const baseIndexData = state._replayBufferedIndexData ?? state._liveIndexDataSnapshot ?? {};
//
//         if (todayBars.length === 0 && baseIndexData[cleanSymbol]?.length) {
//           return state;
//         }
//
//         return {
//           _replayBufferedIndexData: {
//             ...baseIndexData,
//             [cleanSymbol]: todayBars,
//           },
//           indexHistoryVersion: state.indexHistoryVersion + 1,
//         };
//       }
//
//       // A history fetch that lands outside the session (after the close, overnight,
//       // a weekend) filters down to nothing. Writing that empty array over the bars
//       // dailyCache restored is what blanked the price chart after hours.
//       if (todayBars.length === 0 && state.indexData[cleanSymbol]?.length) {
//         return state;
//       }
//
//       return {
//         indexData: {
//           ...state.indexData,
//           [cleanSymbol]: todayBars,
//         },
//         indexHistoryVersion: state.indexHistoryVersion + 1,
//       };
//     }),
//
//   updateIndexData: (symbol, secondUpdate) =>
//     set((state) => {
//       if (state.replayActive) {
//         const baseIndexData = state._replayBufferedIndexData ?? state._liveIndexDataSnapshot ?? {};
//         const baseAggregationState =
//           state._replayBufferedAggregationState ?? state._liveAggregationSnapshot ?? {};
//         const update = buildIndexUpdate(
//           { indexData: baseIndexData, aggregationState: baseAggregationState },
//           symbol,
//           secondUpdate
//         );
//
//         if (!update) return state;
//
//         return {
//           _replayBufferedIndexData: update.indexData,
//           _replayBufferedAggregationState: update.aggregationState,
//         };
//       }
//
//       return buildIndexUpdate(state, symbol, secondUpdate) ?? state;
//     }),
//
//   getIndexData: (symbol) => {
//     const cleanSymbol = symbol.replace('I:', '');
//     return get().indexData[cleanSymbol] || [];
//   },
//
//   clearIndexData: () => set({ indexData: {}, aggregationState: {} }),
//   beginReplay: () =>
//     set((state) => {
//       if (state.replayActive) {
//         return {
//           indexData: {},
//           aggregationState: {},
//         };
//       }
//
//       const indexSnapshot = cloneIndexData(state.indexData);
//       const aggregationSnapshot = cloneAggregationState(state.aggregationState);
//
//       return {
//         replayActive: true,
//         indexData: {},
//         aggregationState: {},
//         _liveIndexDataSnapshot: indexSnapshot,
//         _liveAggregationSnapshot: aggregationSnapshot,
//         _replayBufferedIndexData: cloneIndexData(indexSnapshot),
//         _replayBufferedAggregationState: cloneAggregationState(aggregationSnapshot),
//       };
//     }),
//   setReplayActive: (replayActive) =>
//     set((state) => {
//       if (replayActive) {
//         if (state.replayActive) return state;
//
//         const indexSnapshot = cloneIndexData(state.indexData);
//         const aggregationSnapshot = cloneAggregationState(state.aggregationState);
//
//         return {
//           replayActive: true,
//           _liveIndexDataSnapshot: indexSnapshot,
//           _liveAggregationSnapshot: aggregationSnapshot,
//           _replayBufferedIndexData: cloneIndexData(indexSnapshot),
//           _replayBufferedAggregationState: cloneAggregationState(aggregationSnapshot),
//         };
//       }
//
//       if (!state.replayActive) {
//         return {
//           replayActive: false,
//           _liveIndexDataSnapshot: null,
//           _liveAggregationSnapshot: null,
//           _replayBufferedIndexData: null,
//           _replayBufferedAggregationState: null,
//         };
//       }
//
//       return {
//         replayActive: false,
//         indexData: cloneIndexData(state._replayBufferedIndexData ?? state._liveIndexDataSnapshot ?? {}),
//         aggregationState: cloneAggregationState(
//           state._replayBufferedAggregationState ?? state._liveAggregationSnapshot ?? {}
//         ),
//         _liveIndexDataSnapshot: null,
//         _liveAggregationSnapshot: null,
//         _replayBufferedIndexData: null,
//         _replayBufferedAggregationState: null,
//       };
//     }),
//   reset: () => set(getInitialState()),
// }));
//
// export default useWebSocketDataStore;
//
//
//
//
//
//
//
//
//
//
//
